"use client";
import Link from "next/link";
import React, { ReactNode } from "react";
import { SubheaderSpan } from "../components";

export const useParseLinks = () => {
  const parseLinks = (text?: string): ReactNode[] => {
    if (!text) return [];

    const regex =
      /\[\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)\]\(([^)]+)\)|\[([^\]]+)\]\(([^)]+)\)/g;

    const result: ReactNode[] = [];
    let lastIndex = 0;

    text.replace(
      regex,
      (
        match,
        nestedText,
        url,
        nestedStyles,
        simpleText,
        simpleStyle,
        index
      ) => {
        if (lastIndex < index) {
          result.push(text.slice(lastIndex, index));
        }

        if (nestedText && url && nestedStyles) {
          const { isSubheader, backgroundColor, style } =
            parseStyles(nestedStyles);

          if (isSubheader) {
            result.push(
              <SubheaderSpan
                key={`subheader-${index}`}
                backgroundColor={backgroundColor}
              >
                <Link
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-block transition-colors duration-300"
                  style={{ textDecoration: "none" }}
                >
                  {nestedText}
                  <span
                    className="underline-animation group-hover:animate-slideIn"
                    style={
                      style.color ? { backgroundColor: style.color } : undefined
                    }
                  ></span>
                </Link>
              </SubheaderSpan>
            );
          } else {
            result.push(
              <Link
                key={`link-${index}`}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="group text-gray-700 relative inline-block transition-colors duration-300"
                style={style}
              >
                {nestedText}
                <span
                  className="underline-animation group-hover:animate-slideIn"
                  style={
                    style.color ? { backgroundColor: style.color } : undefined
                  }
                ></span>
              </Link>
            );
          }
        } else if (simpleText && simpleStyle) {
          if (isValidUrl(simpleStyle)) {
            result.push(
              <Link
                key={`link-${index}`}
                href={simpleStyle}
                target="_blank"
                rel="noopener noreferrer"
                className="group text-gray-700 relative inline-block transition-colors duration-300"
              >
                {simpleText}
                <span className="underline-animation group-hover:animate-slideIn"></span>
              </Link>
            );
          } else {
            const { isSubheader, backgroundColor, style } =
              parseStyles(simpleStyle);

            if (isSubheader) {
              result.push(
                <SubheaderSpan
                  key={`subheader-${index}`}
                  backgroundColor={backgroundColor}
                >
                  {simpleText}
                </SubheaderSpan>
              );
            } else {
              result.push(
                <span key={`text-${index}`} style={style}>
                  {simpleText}
                </span>
              );
            }
          }
        }

        lastIndex = index + match.length;
        return "";
      }
    );

    if (lastIndex < text.length) {
      result.push(text.slice(lastIndex));
    }

    return result;
  };

  const parseStyles = (styleString: string) => {
    const style: React.CSSProperties = {};
    let isSubheader = false;
    let backgroundColor: string | undefined = undefined;

    const styleArray = styleString.split(",");
    styleArray.forEach((item) => {
      const trimmed = item.trim();

      if (trimmed === "bold") {
        style.fontWeight = "bold";
      } else if (trimmed === "subheader") {
        isSubheader = true;
        style.fontWeight = "bold";
      } else if (isValidHexColor(trimmed)) {
        if (isSubheader) {
          backgroundColor = trimmed;
        } else {
          style.color = trimmed;
        }
      }
    });

    return {
      isSubheader,
      backgroundColor,
      style,
    };
  };

  const isValidHexColor = (hex: string) =>
    /^#([0-9A-F]{3}){1,2}$/i.test(hex.trim());

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  return { parseLinks };
};
